import { InvoiceForCurrencyForLegalEntity, Transaction, TransactionType } from '@ravago/shared/page-data/models/elements';
import { MultiselectOption } from '@ravago/shared/radiance/components/input-field/components/Multiselect/Multiselect';

import { OpenInvoiceFilters } from '../models/filters.model';

import { isOverDue } from './invoice-date.utils';

export class InvoiceFilterUtils {
   static readonly getUniqueInvoiceTypes = (invoices: InvoiceForCurrencyForLegalEntity[]): TransactionType[] => {
      const allTypes = invoices.flatMap((invoice) =>
         invoice.currencyTransactions.flatMap((currencyInvoice) => currencyInvoice.transactions.map((invoiceItem) => invoiceItem.type))
      );
      return Array.from(new Set(allTypes));
   };

   static readonly getDocumentTypeOptions = (invoices: InvoiceForCurrencyForLegalEntity[]): MultiselectOption[] => {
      const invoiceTypes = InvoiceFilterUtils.getUniqueInvoiceTypes(invoices);
      return invoiceTypes.map((type) => ({
         checked: false,
         id: type,
         label: InvoiceFilterUtils.mapDocumentTypeToLabel(type),
         disabled: false
      }));
   };

   static readonly mapDocumentTypeToLabel = (type: string): string => {
      switch (type) {
         case TransactionType.INVOICE:
            return 'invoices.invoice-type.INVOICE';
         case TransactionType.CREDIT_NOTE:
            return 'invoices.invoice-type.CREDIT_NOTE';
         case TransactionType.DEBIT_NOTE:
            return 'invoices.invoice-type.DEBIT_NOTE';
         case TransactionType.PAYMENT:
            return 'invoices.invoice-type.PAYMENT';
         case TransactionType.CHEQUE:
            return 'invoices.invoice-type.CHEQUE';
         case TransactionType.UNMATCHED_PAYMENT:
            return 'invoices.invoice-type.UNMATCHED_PAYMENT';
         default:
            return type;
      }
   };

   static filterOpenInvoices(
      invoices: InvoiceForCurrencyForLegalEntity[],
      filters: OpenInvoiceFilters,
      excludeDocumentTypes = false
   ): InvoiceForCurrencyForLegalEntity[] {
      return invoices.map((invoice) => ({
         ...invoice,
         currencyTransactions: invoice.currencyTransactions.map((currencyInvoice) => ({
            ...currencyInvoice,
            transactions: this.filterOpenInvoiceTransactions(currencyInvoice.transactions, filters, excludeDocumentTypes)
         }))
      }));
   }

   static filterOpenInvoiceTransactions(transactions: Transaction[], filters: OpenInvoiceFilters, excludeDocumentTypes = false): Transaction[] {
      return transactions.filter((invoiceItem) => {
         const searchMatch = filters.eInvoiceId
            ? invoiceItem.eInvoiceId?.includes(filters.eInvoiceId) ||
              invoiceItem.documentId?.toString().includes(filters.eInvoiceId) ||
              invoiceItem.id.includes(filters.eInvoiceId)
            : true;
         const onlyOverdue = filters.overdue && invoiceItem.dueDate ? isOverDue(invoiceItem.dueDate) && invoiceItem.amount > 0 : true;
         const dueDateMatch = filters.dueDate ? invoiceItem.dueDate && invoiceItem.dueDate <= filters.dueDate && invoiceItem.amount > 0 : true;
         const documentTypeMatch = InvoiceFilterUtils.getDocumentTypeMatch(invoiceItem.type, filters, excludeDocumentTypes);
         return searchMatch && onlyOverdue && dueDateMatch && documentTypeMatch;
      });
   }

   static getDocumentTypeMatch(invoiceType: string, filters: OpenInvoiceFilters, excludeDocumentTypes: boolean): boolean {
      if (excludeDocumentTypes || !filters.documentTypes) {
         return true;
      }
      const { documentTypes } = filters;
      const noTypesChecked = documentTypes.every((type) => !type.checked);
      const typeChecked = documentTypes.some((type) => type.checked && type.id === invoiceType);

      return noTypesChecked || typeChecked;
   }

   static readonly hasOverdueInvoices = (invoices: InvoiceForCurrencyForLegalEntity[]) =>
      invoices.some((invoiceList) =>
         invoiceList.currencyTransactions.some((currencyTransactions) =>
            currencyTransactions.transactions.some((transaction) => transaction.dueDate && isOverDue(transaction.dueDate))
         )
      );
}
